import { createServerFn } from "@tanstack/react-start";
import { notFound } from "@/lib/navigation";
import { FileText } from "lucide-react";
import { NoteBody } from "@/components/portal/note-body";
import { NoteAgentCard } from "@/components/portal/note-agent-card";
import { NoteExhibitLightbox } from "@/components/portal/note-exhibit-lightbox";
import { NoteReaderPanel } from "@/components/portal/note-reader-panel";
import { formatNoteDate } from "@/components/portal/note-card";
import { NotesFeedFilters } from "@/components/portal/notes-feed-filters";
import { NotesFeedList } from "@/components/portal/notes-feed-list";
import { AtlasNotesWebMcp } from "@/components/portal/atlas-notes-webmcp";
import { NoteTaxonomyBadges } from "@/components/portal/note-taxonomy-badges";
import { PublicNotesPreview } from "@/components/portal/public-notes-preview";
import { loadPublicPortalPreview } from "@/components/portal/public-portal-home.server";
import {
  getCachedInternalNoteForPortal,
  listNotesFeedPageForPortal,
  listNotesFeedTagSuggestionsForPortal,
} from "@/lib/notes-feed";
import {
  notesFeedFilterSearchParams,
  parseNotesFeedFilters,
} from "@/lib/notes-feed-filters";
import { getCurrentPortalRenderContext } from "@/lib/portal-page-access";
import { requirePortalShellContext } from "@/lib/portal-shell-context";
import { validatePageLoadInput } from "@/lib/page-load";
import { cn } from "@/lib/utils";
import { buildNoteSocialMetadata } from "@/lib/notes-social-metadata";
import type { GhostNote } from "@/lib/ghost/types";

// The notes page is a reading surface: the feed is a centered column, and an
// internal note opens in a reader that slides over it from the right (on
// phones the reader replaces the feed). The page runs in the shell's fullBleed
// mode (see portal-dashboard-shell), so the feed and reader own their scroll.
// The first page and selected internal note are rendered from Convex on the
// server; older pages stream in as the feed scrolls. Selection travels through
// the `?note=` search param, and nothing opens until a reader picks a note.
// `/notes/[slug]` still resolves for existing links and bookmarks, and hosts
// the `/pdf` print sheet, but nothing links to it any more.

const PUSH_SPACER_CLASS =
  "hidden basis-0 grow transition-[flex-grow] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none lg:block";

function NoteViewer({
  note,
  exhibitLightbox,
  agentPromptLinksEnabled,
}: {
  note: GhostNote;
  /** `note-exhibit-lightbox` decision, resolved once per request. */
  exhibitLightbox: boolean;
  agentPromptLinksEnabled: boolean;
}) {
  const date = formatNoteDate(note.publishedAt);
  const author = note.authors[0]?.name;
  const meta = [
    author,
    date,
    note.readingTime ? `${note.readingTime} min read` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <article className="min-w-0">
      <div className="flex flex-wrap items-center justify-between gap-3 atlas-v2:min-w-0">
        <NoteTaxonomyBadges linked taxonomy={note.taxonomy} />
        <div className="flex shrink-0 items-center gap-4">
          {/* New tab: the sheet opens its own print dialog, so keeping the
              reader's place in the feed matters. */}
          <a
            className="flex shrink-0 items-center gap-1 text-xs font-semibold text-primary/80 transition-colors hover:text-primary"
            href={`/notes/${note.slug}/pdf`}
            rel="noopener noreferrer"
            target="_blank"
          >
            Open PDF
            <FileText className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
      <h1 className="mt-4 text-2xl font-semibold tracking-tight text-foreground sm:text-[28px] sm:leading-9 atlas-v2:[overflow-wrap:anywhere]">
        {note.title}
      </h1>
      {meta ? (
        <div className="mt-2 text-sm text-muted-foreground">{meta}</div>
      ) : null}
      {/* Note body is authored in Ghost by trusted staff, rendered directly. */}
      <div className="mt-8">
        <NoteBody html={note.html} />
      </div>
      {agentPromptLinksEnabled ? (
        <NoteAgentCard slug={note.slug} title={note.title} />
      ) : null}
      {exhibitLightbox ? <NoteExhibitLightbox /> : null}
    </article>
  );
}

export const loadPage = createServerFn({ method: "GET" })
  .validator(validatePageLoadInput)
  .handler(async ({ data: { searchParams } }) => {
    const metadata = await buildNoteSocialMetadata(searchParams.note);
    const context = await getCurrentPortalRenderContext();
    if (!context) {
      return {
        kind: "public" as const,
        metadata,
        preview: await loadPublicPortalPreview(),
        selectedSlug:
          typeof searchParams.note === "string" ? searchParams.note : undefined,
      };
    }
    const { featureDecisions: decisions } =
      await requirePortalShellContext("/notes");
    if (!decisions.notesFeedEnabled) {
      notFound();
    }

    const filters = parseNotesFeedFilters(searchParams);
    const [firstPage, tagSuggestions] = await Promise.all([
      listNotesFeedPageForPortal(context.session, {
        cursor: null,
        limit: 30,
        search: filters.search || undefined,
        sourceKeys: filters.sourceKeys,
        sort: filters.sort,
        tagKey: filters.tagKey || undefined,
        scheduleRefresh: true,
      }),
      listNotesFeedTagSuggestionsForPortal(context.session),
    ]);
    const filterSearchParams = notesFeedFilterSearchParams(filters);
    const filterQuery = filterSearchParams.toString();
    const requested =
      typeof searchParams.note === "string" ? searchParams.note : undefined;
    const selected = requested
      ? await getCachedInternalNoteForPortal(context.session, requested)
      : null;
    const hasResults = firstPage.page.length > 0 || !firstPage.isDone;

    return {
      kind: "member" as const,
      metadata,
      filters,
      firstPage,
      tagSuggestions,
      filterQuery,
      selected,
      hasResults,
      webMcpEnabled: decisions.atlasWebMcpEnabled,
      agentPromptLinksEnabled:
        decisions.agentPromptLinksEnabled &&
        context.pageAccess.basicChatEnabled &&
        context.pageAccess.accessiblePageKeys.includes("agent"),
      exhibitLightbox: decisions.noteExhibitLightboxEnabled,
    };
  });

export default function NotesPage({
  data,
}: {
  data: Awaited<ReturnType<typeof loadPage>>;
}) {
  if (data.kind === "public") {
    return (
      <PublicNotesPreview
        preview={data.preview}
        selectedSlug={data.selectedSlug}
      />
    );
  }
  const {
    filters,
    firstPage,
    tagSuggestions,
    filterQuery,
    selected,
    hasResults,
  } = data;
  const closeHref = filterQuery ? `/notes?${filterQuery}` : "/notes";

  return (
    <div className="safe-x relative flex min-h-0 flex-1 atlas-v2:min-w-0">
      <AtlasNotesWebMcp enabled={data.webMcpEnabled} />
      {/* The feed is the page. Opening a note slides it left into a rail and
          the reader takes the rest; phones show one or the other. The spacers
          and feed width transition so the feed moves rather than jumps. */}
      <div aria-hidden className={cn(PUSH_SPACER_CLASS, selected && "grow-0")} />
      <aside
        aria-label="Notes feed"
        className={cn(
          "min-h-0 w-full min-w-0 flex-col border-border/60 lg:flex lg:bg-card atlas-v2:bg-sidebar atlas-v2:lg:bg-card",
          "lg:transition-[width] lg:duration-300 lg:ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
          selected
            ? "hidden lg:w-80 lg:shrink-0 lg:border-r xl:w-96"
            : "flex lg:w-3xl lg:border-x",
        )}
      >
        <div className="shrink-0 border-b border-border/60 p-3">
          <NotesFeedFilters
            key={filterQuery || "all"}
            initialSearch={filters.search}
            initialSort={filters.sort}
            initialSourceKeys={filters.sourceKeys}
            initialTagKey={filters.tagKey}
            sources={firstPage.sources}
            tags={tagSuggestions}
          />
        </div>
        {hasResults ? (
          <NotesFeedList
            filterQuery={filterQuery}
            initialCursor={firstPage.continueCursor}
            initialDone={firstPage.isDone}
            initialItems={firstPage.page}
            key={filterQuery || "all"}
            selectedSlug={selected?.slug}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center px-6 py-10 text-center text-sm leading-6 text-muted-foreground">
            {filters.search ||
            filters.tagKey ||
            filters.sourceKeys.length < firstPage.sources.length
              ? "No articles match this search yet."
              : "The feed cache is warming. Published articles will appear here shortly."}
          </div>
        )}
      </aside>

      <div aria-hidden className={cn(PUSH_SPACER_CLASS, selected && "grow-0")} />
      {selected ? (
        <NoteReaderPanel closeHref={closeHref} slug={selected.slug}>
          <NoteViewer
            agentPromptLinksEnabled={data.agentPromptLinksEnabled}
            exhibitLightbox={data.exhibitLightbox}
            note={selected}
          />
        </NoteReaderPanel>
      ) : null}
    </div>
  );
}
